import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { captureException } from '@sentry/ember';
import { validatePresence } from 'ember-changeset-validations/validators';
import { task, timeout } from 'ember-concurrency';
import teamValidations from 'scaleflow/validators/team';

const DEFUALT_TIMEZONE = 'GMT0';

/** Quiet period before a text edit is committed. */
export const AUTOSAVE_DEBOUNCE_MS = 800;
/** One retry, for transient failures only. */
export const AUTOSAVE_RETRY_BACKOFF_MS = 2000;

export default class TeamsTeamSettingsController extends Controller {
  @service('beta-toggle') betaToggle;
  @service router;
  @service notifications;
  @service intl;

  // This used to read `this.teamValidations` — a self-reference that evaluated to
  // undefined, so the changeset was built with no validation map and `validate()`
  // was a no-op. Autosave must never PATCH an invalid record.
  //
  // A getter rather than a class field: field initialisers run during construction,
  // where reading an injected service is unreliable. (`settings/teams/create.js`
  // hardcodes its message in a field, which is probably why.)
  get teamValidations() {
    return {
      ...teamValidations,
      name: [
        validatePresence({
          presence: true,
          message: this.intl.t('_teams.name-error'),
        }),
      ],
    };
  }

  @tracked time = '';
  @tracked meridiem = 'am';
  @tracked timezone = DEFUALT_TIMEZONE;

  /** Set by the route's setupController. */
  @tracked changeset;

  // Autosave owns its own dirty signal instead of reading `changeset.isDirty`.
  // `EmberChangeset#execute()` overrides the base implementation and drops the
  // `this[CHANGES] = {}` line, so a committed changeset stays dirty forever — a
  // task keyed off isDirty would re-fire in a loop and never settle on "Saved".
  @tracked editSeq = 0;
  @tracked savedSeq = 0;
  @tracked lastError = null;

  // Injectable so tests do not have to sleep. Deliberately plain fields rather than
  // a build-time isTesting() macro: CLAUDE.md documents that such flags never fire
  // in this pipeline, because test:ember builds with --mode development.
  debounceMs = AUTOSAVE_DEBOUNCE_MS;
  retryBackoffMs = AUTOSAVE_RETRY_BACKOFF_MS;

  get isSaving() {
    return this.autosave.isRunning;
  }

  get hasUnsavedEdits() {
    return this.editSeq !== this.savedSeq;
  }

  /** Drives the inline status marker. */
  get saveState() {
    if (this.lastError) return 'error';
    if (this.isSaving) return 'saving';
    if (this.changeset?.get('isInvalid')) return 'invalid';
    if (this.hasUnsavedEdits) return 'pending';
    if (this.savedSeq > 0) return 'saved';
    return 'idle';
  }

  get showExtraOptions() {
    return this.betaToggle.uncertainties || this.betaToggle.wfa;
  }

  /**
   * The save primitive.
   *
   * `enqueue` rather than `restartable`: restarting would abandon an in-flight
   * `model.save()` we can no longer observe — the request still lands, but we lose
   * the outcome, the error state and the retry. maxConcurrency 1 keeps two saves off
   * the same Ember Data record. Debouncing lives in the timeout at the head, so a
   * queued duplicate collapses into the one that runs.
   *
   * Deliberately never calls `changeset.save()`. That helper is
   * execute() -> content.save() -> await -> rollback(), and the rollback wipes the
   * buffer *after* the request resolves — so anything typed during the flight is
   * destroyed, and the re-notify rewrites the live input's value, dropping the
   * character and jumping the caret.
   */
  autosave = task(
    { enqueue: true, maxConcurrency: 1 },
    async (wait = false) => {
      if (wait) await timeout(this.debounceMs);

      const changeset = this.changeset;
      if (!changeset || !this.hasUnsavedEdits) return;

      const seq = this.editSeq;

      // Validate only the keys that actually changed. A bare validate() runs the whole
      // map, which would flash an error on an untouched field the moment someone edits
      // an unrelated one.
      const keys = changeset.changes.map(({ key }) => key);
      if (keys.length) await changeset.validate(...keys);
      if (changeset.get('isInvalid')) return;

      changeset.execute();

      try {
        await this.model.save();
        this.savedSeq = seq;
        this.lastError = null;
      } catch (error) {
        if (this.isRetryable(error)) {
          await timeout(this.retryBackoffMs);
          try {
            await this.model.save();
            this.savedSeq = seq;
            this.lastError = null;
            return;
          } catch (retryError) {
            this.failSave(changeset, retryError);
            return;
          }
        }
        this.failSave(changeset, error);
      }
    },
  );

  /**
   * Keep the typed value and surface a Retry. Reverting text someone is still
   * editing is worse than leaving it unsaved, and the changeset buffer plus the
   * dirty record are already in the right state for another attempt.
   */
  failSave(changeset, error) {
    this.lastError = error;
    this.applyServerErrors(changeset, error);
    captureException(error);
  }

  /**
   * Transient failures only. Retrying a 4xx on a debounce loop turns a validation
   * failure into an API hammer, and 401/403 is already refreshed and replayed once
   * by the application adapter — so one reaching us is terminal.
   */
  isRetryable(error) {
    const status = Number(error?.status ?? error?.errors?.[0]?.status);
    if (!status) return true;
    return status >= 500;
  }

  /**
   * Map a JSON:API validation response back onto the fields, so a server-side
   * rejection renders in the input rather than only in the status marker.
   */
  applyServerErrors(changeset, error) {
    const errors = error?.errors;
    if (!Array.isArray(errors)) return;
    errors.forEach(({ source, detail, title }) => {
      const pointer = source?.pointer;
      if (!pointer) return;
      const key = pointer.split('/').pop();
      if (!key) return;
      changeset.addError(key, detail ?? title);
    });
  }

  /**
   * Record that something actually changed. This is the only dirty signal autosave
   * trusts — see the note on `editSeq`.
   */
  markEdited() {
    this.editSeq++;
    this.lastError = null;
  }

  /** Text fields, per keystroke: coalesce into one debounced save. */
  @action
  scheduleAutosave() {
    this.markEdited();
    this.autosave.perform(true);
  }

  /**
   * Blur and form submit: commit whatever is outstanding, now, skipping the debounce.
   * Does NOT mark an edit — leaving a field you did not change must not PATCH, and
   * `changeset.isDirty` cannot tell us that (it stays true forever once execute()
   * has run), so the seq counters are the only thing that can.
   */
  @action
  flushAutosave() {
    if (!this.hasUnsavedEdits) return;
    return this.autosave.perform(false);
  }

  /** Discrete controls — toggles, time picker, membership: commit immediately. */
  @action
  commitNow() {
    this.markEdited();
    return this.autosave.perform(false);
  }

  @action
  handleSubmit(event) {
    event.preventDefault();
    this.flushAutosave();
  }

  @action
  retrySave() {
    this.lastError = null;
    return this.autosave.perform(false);
  }

  @action
  async archiveTeam() {
    // Let any pending or in-flight autosave finish first — two overlapping saves on
    // one Ember Data record is exactly what the enqueued task exists to prevent.
    await this.autosave.last;

    const team = this.model;
    team.active = false;
    await team.save();
    this.router.transitionTo('settings.teams.archived');
    this.notifications.notificationMessage = this.intl.t(
      '_teams.archived-success',
    );
    this.notifications.toggleShowNotification();
  }

  convertTo24HourFormat(time, meridiem) {
    // The guard used to be `length !== 2 && !meridiem`, which only bailed when BOTH
    // were missing — so with a meridiem set and no time it fell through and returned
    // '12:undefined'. Truthy, and it used to reach the API.
    let [hour, minutes] = String(time ?? '').split(':');
    if (!hour || !minutes || !meridiem) return '';
    if (+hour === 12 && meridiem === 'am') hour = '00';
    else if (+hour < 12 && meridiem === 'pm') hour = `${+hour + 12}`;
    return `${hour}:${minutes}`;
  }

  @action
  handleTimezoneSelect(e) {
    this.timezone = e;
  }

  @action
  handleTimeSelect(changeset, e) {
    this.time = e.value;
    this.setArchiveTime(changeset);
  }

  @action
  handleMeridiemSelect(e, changeset) {
    this.meridiem = e;
    this.setArchiveTime(changeset);
  }

  /**
   * `convertTo24HourFormat('', meridiem)` returns an empty string, so writing it
   * unconditionally would blank an already-saved archive time the moment someone
   * clicks AM/PM without picking a time first. Harmless while a Save button gated
   * the write; not harmless once the form autosaves.
   */
  setArchiveTime(changeset) {
    if (!this.autoArchiveTime) return;
    changeset.set('archive_time', this.autoArchiveTime);
    this.commitNow();
  }

  /**
   * Membership is written straight to the model, never buffered on the changeset.
   *
   * Three reasons. `Dropdowns::Users#handleCheckboxToggle` already push-mutates the
   * live ManyArray before `onSelect` fires, so the buffer was never the source of
   * truth. Nothing rolls back a hasMany, so buffering buys nothing. And the member
   * list renders from `TeamModel#activeMembers` — the model — so buffering meant the
   * list and the buffer could disagree, which is why removals used not to show up
   * until Save.
   */
  @action
  async setMembers(people) {
    await this.persistMembers([...(people ?? [])]);
  }

  @action
  async removeMember(member) {
    // Filter the full roster, not `activeMembers` — the latter would silently drop
    // every inactive member from the relationship.
    const people = (await this.model.people).slice();
    await this.persistMembers(people.filter((m) => m.id !== member.id));
  }

  async persistMembers(next) {
    const current = (await this.model.people).slice();
    if (!this.isSafeMembershipChange(current, next)) return;
    this.model.people = next;
    // Through the same task, so a membership write cannot overlap an attribute save
    // on this record. The task commits the changeset buffer too, which is harmless:
    // execute() re-merging already-saved values is idempotent.
    await this.commitNow();
  }

  /**
   * Every interaction in this UI toggles exactly one person, so a jump from a
   * multi-member roster straight to empty is never something the user asked for —
   * it means we read an unresolved relationship as `[]`. Refuse it. With no Save
   * button there is no human checkpoint left to catch it.
   */
  isSafeMembershipChange(current, next) {
    return next.length > 0 || current.length <= 1;
  }

  get autoArchiveTime() {
    return this.convertTo24HourFormat(this.time, this.meridiem);
  }

  @action
  togglePreferences(changeset, id, e) {
    changeset.set(id, e.target.checked);
    this.commitNow();
  }
}
